/* ═══════════════════════════════════════════════════════
   Moonara Cart — drawer + ajax cart + add-to-cart interceptor
   ═══════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const DRAWER_SELECTOR = '#MoonaraCartDrawer';
  const BODY_OPEN_CLASS = 'moonara-cart-open';

  // ── Drawer open/close ─────────────────────────────────
  function openDrawer() {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_OPEN_CLASS);
    loadRecommendations();
  }

  function closeDrawer() {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_OPEN_CLASS);
  }

  // ── Refresh drawer contents from server ───────────────
  async function refreshCart() {
    try {
      const res = await fetch('/?section_id=cart-drawer-contents');
      if (res.ok) {
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('.shopify-section') || doc.body;
        const body = document.querySelector('[data-cart-body]');
        if (body && fresh) {
          // Section Rendering API returns the section wrapper. Get inner content only.
          body.innerHTML = fresh.innerHTML;
        }
      }
    } catch (e) {
      // Fallback: just reload the drawer contents via a snippet endpoint.
      try {
        const r = await fetch(window.location.pathname);
        const t = await r.text();
        const doc = new DOMParser().parseFromString(t, 'text/html');
        const fresh = doc.querySelector('[data-cart-body]');
        const body = document.querySelector('[data-cart-body]');
        if (body && fresh) body.innerHTML = fresh.innerHTML;
      } catch (_) {}
    }
  }

  // Alternative: render cart-drawer snippet by fetching the cart page and slicing its body
  // (section_id only works for SECTIONS, not snippets; we use a section called cart-drawer-snippet if needed)

  // Simpler reliable refresh: fetch cart.js JSON and rebuild the drawer body client-side.
  async function refreshCartFromJson() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      renderDrawerBody(cart);
    } catch (e) {
      console.warn('Cart refresh failed:', e);
    }
  }

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function renderDrawerBody(cart) {
    const body = document.querySelector('[data-cart-body]');
    if (!body) return;

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = `
        <div class="moonara-cart-drawer__empty">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="moonara-cart-drawer__continue">Continue Shopping</a>
        </div>`;
      return;
    }

    const itemsHtml = cart.items.map((item, i) => {
      const optionsHtml = (item.options_with_values || [])
        .filter(opt => opt.value && opt.value !== 'Default Title')
        .map(opt => `<div class="moonara-cart-item__option"><strong>${opt.name}:</strong> ${escapeHtml(opt.value)}</div>`)
        .join('');
      const imgUrl = item.image ? item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_320x$1') : '';
      return `
        <div class="moonara-cart-item" data-cart-item data-line-key="${item.key}" data-line-index="${i + 1}">
          <a href="${item.url}" class="moonara-cart-item__media">
            ${imgUrl ? `<img src="${imgUrl}" alt="${escapeHtml(item.product_title)}" loading="lazy" width="160" height="160">` : ''}
          </a>
          <div class="moonara-cart-item__details">
            <a href="${item.url}" class="moonara-cart-item__title">${escapeHtml(item.product_title)}</a>
            ${optionsHtml ? `<div class="moonara-cart-item__options">${optionsHtml}</div>` : ''}
            <div class="moonara-cart-item__row">
              <div class="moonara-cart-item__qty">
                <button type="button" class="moonara-cart-item__qty-btn" data-qty-minus aria-label="Decrease quantity">−</button>
                <input type="number" class="moonara-cart-item__qty-input" value="${item.quantity}" min="0" aria-label="Quantity" data-qty-input>
                <button type="button" class="moonara-cart-item__qty-btn" data-qty-plus aria-label="Increase quantity">+</button>
              </div>
              <div class="moonara-cart-item__price">${formatMoney(item.final_line_price)}</div>
            </div>
          </div>
        </div>`;
    }).join('');

    const firstProductId = cart.items[0].product_id;

    body.innerHTML = `
      <div class="moonara-cart-drawer__items">${itemsHtml}</div>
      <section class="moonara-cart-recommendations" data-cart-recommendations data-product-id="${firstProductId}">
        <h3 class="moonara-cart-recommendations__title">We Also Recommend</h3>
        <div class="moonara-cart-recommendations__grid" data-recommendations-grid></div>
      </section>
      <footer class="moonara-cart-drawer__footer">
        <div class="moonara-cart-drawer__subtotal">
          <span class="moonara-cart-drawer__subtotal-label">Subtotal</span>
          <span class="moonara-cart-drawer__subtotal-value">${formatMoney(cart.total_price)}</span>
        </div>
        <a href="/checkout" class="moonara-cart-drawer__checkout-btn">Check Out</a>
      </footer>`;

    loadRecommendations();
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // ── Load product recommendations ──────────────────────
  async function loadRecommendations() {
    const container = document.querySelector('[data-cart-recommendations]');
    if (!container) return;
    const grid = container.querySelector('[data-recommendations-grid]');
    if (!grid || grid.dataset.loaded === '1') return;
    const productId = container.dataset.productId;
    if (!productId) return;

    try {
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=3&intent=related`);
      const data = await res.json();
      if (data.products && data.products.length) {
        grid.innerHTML = data.products.map(p => `
          <a href="${p.url}" class="moonara-cart-recommendation-card">
            <div class="moonara-cart-recommendation-card__media">
              ${p.featured_image ? `<img src="${p.featured_image.replace(/(\.[a-z]+)(\?.*)?$/i, '_400x$1')}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
            </div>
            <div class="moonara-cart-recommendation-card__title">${escapeHtml(p.title)}</div>
          </a>`).join('');
        grid.dataset.loaded = '1';
      } else {
        container.style.display = 'none';
      }
    } catch (e) {
      container.style.display = 'none';
    }
  }

  // ── Quantity changes ──────────────────────────────────
  async function updateLine(lineIndex, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity }),
      });
      const cart = await res.json();
      renderDrawerBody(cart);
    } catch (e) {
      console.warn('Cart change failed:', e);
    }
  }

  // ── Intercept clicks ──────────────────────────────────
  document.addEventListener('click', (e) => {
    // Close
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Qty +/-
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (minus || plus) {
      e.preventDefault();
      const item = (minus || plus).closest('[data-cart-item]');
      const input = item?.querySelector('[data-qty-input]');
      if (!input || !item) return;
      const lineIndex = parseInt(item.dataset.lineIndex, 10);
      let qty = parseInt(input.value, 10) || 0;
      qty = minus ? Math.max(0, qty - 1) : qty + 1;
      input.value = qty;
      updateLine(lineIndex, qty);
    }
  });

  // Qty input direct change
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const item = input.closest('[data-cart-item]');
    if (!item) return;
    const lineIndex = parseInt(item.dataset.lineIndex, 10);
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    updateLine(lineIndex, qty);
  });

  // Esc closes drawer
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });

  // ── Intercept Add to Cart forms ───────────────────────
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.match(/\/cart\/add/)) return;

    e.preventDefault();
    const formData = new FormData(form);
    // Tell Shopify to return JSON
    if (!formData.has('sections')) formData.set('sections', 'cart-drawer-contents');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.description || err.message || 'Could not add to cart.');
        return;
      }
      await refreshCartFromJson();
      openDrawer();
    } catch (err) {
      console.warn('Add to cart failed:', err);
      alert('Could not add to cart. Please try again.');
    }
  });

  // Allow other code / links to open the drawer programmatically
  window.MoonaraCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCartFromJson,
  };

  // If we land on the page with cart drawer present, do a soft refresh once
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector(DRAWER_SELECTOR)) {
      // Initial render is already there from Liquid; recommendations get loaded when drawer opens
    }
  });
})();
